import { Component } from 'react';
import { func, oneOfType, object } from 'prop-types';
import { dequal } from 'dequal';
import omit from 'lodash/omit';

import { CartItemPropType } from 'Types/cart';
import ProductPropType from 'Types/product';
import { API_PRODUCTS } from 'Constants';
import { stockAlertWishlistId } from 'Contexts/WishlistContext';

export default class ProductAbstract extends Component {
  constructor(props) {
    super(props);
    const { product = {} } = props;
    const productId = product?.id || product?.productId || product?.itemId;
    this.state = {
      shouldUpdate: false,
      /* eslint-disable react/no-unused-state */
      loadedProduct: {},
      detailsLoading: false,
      addToCartLoading: false,
      /* eslint-enable react/no-unused-state */
      product: {
        ...(product || {}),
        productId,
        quantity: product?.quantity || product?.minQuantity || 1,
        // TODO: sort promotions by expiry date
        promotions: ProductAbstract.composePromotions(product)
      }
    };

    this.handleOnAddToCart = this.handleOnAddToCart.bind(this);
    this.executeAddToCart = this.executeAddToCart.bind(this);
    this.handleOnIQuoteView = this.handleOnIQuoteView.bind(this);
    this.handleOnQuickView = this.handleOnQuickView.bind(this);
    this.handleOnAddToWishlist = this.handleOnAddToWishlist.bind(this);
    this.handleOnChangeQuantity = this.handleOnChangeQuantity.bind(this);
    this.handleStockAlertModalOpen = this.handleStockAlertModalOpen.bind(this);
  }

  static composePromotions = product => (product?.appliedPromotions || [])
    .reduce((acc, promotion) => {
      const { erpPromotionId, products } = promotion;
      const promotionDetails = Object.values(product.promotions || [])
        .flatMap(v => v)
        .find(promo => promo.promotionId === erpPromotionId);
      if (
        !promotionDetails
        || !products?.length
      ) return acc;
      if (!(promotionDetails.type in acc)) {
        acc[promotionDetails.type] = [];
      }
      const productPrices = products.reduce((prices, prod) => {
        /* eslint-disable no-param-reassign */
        prices.totalPrice += prod.price * prod.quantity;
        prices.totalPromoPrice += prod.totalPrice;
        /* eslint-enable no-param-reassign */
        return prices;
      }, {
        totalPrice: 0,
        totalPromoPrice: 0
      });
      acc[promotionDetails.type].push({
        ...productPrices,
        ...promotion,
        ...promotionDetails,
        products,
      //   products: products.sort((a, b) => {
      //     if (a.mainProduct) {
      //       return -1;
      //     }
      //     if (b.mainProduct) {
      //       return 1;
      //     }
      //     if (a.mainProductId === (product.productId || product.id)) {
      //       return -1;
      //     }
      //     if (b.mainProductId === (product.productId || product.id)) {
      //       return 1;
      //     }
      //     return (b.price * b.quantity) - (a.price * a.quantity);
      //   })
      });
      return acc;
    }, {});

  static getDerivedStateFromProps(props, state) {
    if (
      (
        props.product.currency
        && props.product.currency !== state.product.currency
      )
      || props.product.price !== state.product.price
      || (
        // eslint-disable-next-line no-restricted-globals
        !isNaN(props.product.quantity)
        && props.product.quantity !== state.product.quantity
      )
    ) {
      return {
        ...state,
        shouldUpdate: true,
        product: {
          ...state.product,
          promotions: ProductAbstract.composePromotions(props.product),
          quantity: // eslint-disable-next-line no-restricted-globals
            !isNaN(props.product.quantity)
              ? props.product.quantity
              : state.product.quantity,
          currency: props.product.currency,
          price: props.product?.price,
          promoPrice: props.product?.promoPrice,
          priceScales: props.product?.priceScales,
          catalogPrice: props.product?.catalogPrice,
          vatPercent: props.product?.vatPercent,
          greenStampValue: props.product?.greenStampValue
        }
      };
    }
    return {
      ...state,
      shouldUpdate: false
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      !!nextState.shouldUpdate
      || this.state.loading !== nextState.loading
      || this.state.error !== nextState.error
      || this.state.detailsLoading !== nextState.detailsLoading
      || this.state.addToCartLoading !== nextState.addToCartLoading
      || this.state.promotionLoading !== nextState.promotionLoading
      || this.state.addToWishlistLoading !== nextState.addToWishlistLoading
      || !dequal(this.state.product.promotions, nextState.product.promotions)
      || !dequal(this.state.loadedProduct, nextState.loadedProduct)
      || !dequal(this.props.modalsContext, nextProps.modalsContext)
    );
  }

  async handleOnIQuoteView() {
    const { fetch, toast } = this.props;
    const { product } = this.state;

    try {
      this.setState({ iQuoteLoading: true });
      let pathname = '/products';
      if (typeof window !== 'undefined') {
        pathname = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      }
      // eslint-disable-next-line no-unused-vars
      const response = await fetch(`${API_PRODUCT_IQUOTE}?code=${product.code}&ref=${pathname}`);
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: err.info?.error?.message || err.info?.message || 'Could not load iQuote data'
      });
    } finally {
      this.setState({ iQuoteLoading: false });
    }
  }

  /**
   * ADD PRODUCT TO CART
   * @returns {Promise<unknown>}
   */
  async handleOnAddToCart() {
    const { product } = this.state;
    const { modalsContext } = this.props;

    if (!product.hasPrice) {
      modalsContext.setState(modalsContext.modalState.ADD_EXTERNAL_SUPPLIER_PRODUCT_TO_CART.key, {
        open: true,
        product,
        onConfirm: () => this.executeAddToCart()
      });
      return null;
    }

    return this.executeAddToCart();
  }

  /**
   * Execute the actual add to cart logic
   * @returns {Promise<unknown>}
   */
  // eslint-disable-next-line react/sort-comp
  async executeAddToCart() {
    const { product } = this.state;
    const { cart, fetch, t, toast, modalsContext, onAddToCart, userContext: { data: { defaultCurrency, withVat } } } = this.props;
    const cartProduct = (cart?.data?.items || [])
      .find(item => item.productId === product.id && !item.promotion?.promotionId);
    const quantity = Math.max(product.minQuantity, product.quantity);

    if (((cartProduct?.quantity || 0) + quantity) > product.stockValue) {
      toast.addNotification({
        type: 'warning',
        title: t('products.insufficientStock')
      });
      return null;
    }
    this.setState({ addToCartLoading: true });

    try {
      const [updated, crossProducts] = await Promise.allSettled([
        cart.updateProduct({
          ...product,
          id: product.id,
          quantity: Math.min(
            product.stockValue,
            (cartProduct?.quantity || 0) + quantity
          ),
        }),
        fetch(`${ API_PRODUCTS }/${ product.id }/cross-sells?currency=${ defaultCurrency }&vat=${ withVat }`)
      ]);
      if (updated.status === 'fulfilled') {
        if (updated.value === null) {
          toast.addNotification({
            type: 'warning',
            title: t('products.dropShipmentWarning')
          });
        } else {
          toast.addNotification({
            title: product.promotion
              ? t('cart.successfullyAddedPromotionToCart')
              : t('cart.successfullyAddedToCart')
          });
          if (typeof onAddToCart === 'function') {
            onAddToCart(product);
          }
        }
      } else {
        toast.addNotification({
          type: 'error',
          title: t('cart.failedAddToCart'),
          message: updated.value?.errors?.at(0)?.message
        });
        return null;
      }
      if (
        Array.isArray(crossProducts.value)
        && crossProducts.value.length
      ) {
        modalsContext.setState(modalsContext.modalState.CROSS_SALES.key, {
          open: true,
          products: crossProducts.value,
          product
        });
      }
      return updated.value;
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: t('cart.failedAddToCart'),
        message: err.info?.errors?.at(0)?.message
      });
    } finally {
      this.setState({ addToCartLoading: false });
    }
    return null;
  }

  async handleOnQuickView(evt) {
    const { fetch, modalsContext, userContext } = this.props;
    const { loadedProduct } = this.state;
    let prod = evt.id ? evt : this.state.product;
    let pricePayload = {
      ronPrice: prod.ronPrice,
      ronPromoPrice: prod.ronPromoPrice,
      price: prod.price,
      promoPrice: prod.promoPrice,
      catalogPrice: prod.catalogPrice,
      totalPrice: prod.totalPrice,
      ronTotalPrice: prod.ronTotalPrice
    };
    const _this = this;

    if (prod.promoItem) {
      if (prod.id === this.state.product.id) {
        prod = this.state.product;
      } else {
        pricePayload = {};
        prod = omit(
          prod,
          ['minQuantity', 'quantity', 'price', 'ronPrice', 'promoPrice', 'ronPromoPrice']
        );
        if (loadedProduct[prod.id]) {
          loadedProduct[prod.id] = omit(
            loadedProduct[prod.id],
            ['minQuantity', 'quantity', 'price', 'ronPrice', 'promoPrice', 'ronPromoPrice']
          );
        }
      }
    }

    modalsContext.setState(modalsContext.modalState.QUICK_VIEW.key, {
      open: true,
      product: {
        ...prod,
        ...pricePayload,
        ...(loadedProduct[prod.id] || {})
      }
    });

    if (
      loadedProduct
      && !prod.promoItem
      && prod.id in loadedProduct
      && loadedProduct[prod.id].detailsLoaded
      && loadedProduct[prod.id].price === prod.price
    ) {
      return;
    }

    try {
      const data = await fetch(`${ API_PRODUCTS }/${ prod.id }/quick-view?currency=${ userContext?.data?.defaultCurrency }&vat=${ userContext?.data?.withVat }`);
      this.setState(prevState => ({
        ...prevState,
        loadedProduct: {
          ...prevState.loadedProduct,
          [prod.id]: {
            ...(prevState.loadedProduct[prod.id] || {}),
            ...(data || {}),
            quantity: Math.max(data?.minQuantity, 1),
            detailsLoaded: true
          }
        },
        detailsLoading: false
      }), () => {
        const updatedProduct = {
          ...prod,
          ...(data || {}),
          quantity: Math.max(data?.minQuantity, 1),
          detailsLoaded: true
        };
        if (_this.props.modalsContext.modalState.QUICK_VIEW.key) {
          _this.props.modalsContext.setState(modalsContext.modalState.QUICK_VIEW.key, {
            product: updatedProduct
          });
        }
      });
    } catch (err) {
      if (_this.props.modalsContext.modalState.QUICK_VIEW.key) {
        modalsContext.setState(modalsContext.modalState.QUICK_VIEW.key, {
          error: err.message
        });
      }
    }
  }

  /**
   * Add product to wishlist
   * @param selectedItem
   * @returns {Promise<null|*>}
   */
  async handleOnAddToWishlist({ selectedItem = null }) {
    const { product } = this.state;
    const { wishlistContext, t, toast } = this.props;
    const listId = selectedItem?.id || wishlistContext.defaultWishlist?.id;
    if (!listId || selectedItem?.createButton) return null;
    let existingWishlist = wishlistContext?.data[listId] || { products: [] };
    if (listId === wishlistContext.stockAlertWishlist.id) {
      existingWishlist = wishlistContext.stockAlertWishlist;
    }
    const existingProduct = (existingWishlist.products || []).find(prod => prod.id === product.id);
    if (existingProduct) {
      toast.addNotification({
        type: 'info',
        title: t('wishlists.productAlreadyExists')
      });
      return null;
    }
    this.setState({ addToWishlistLoading: true });
    try {
      const r = await wishlistContext.updateProduct(listId, {
        id: product.id,
        quantity: product.quantity || product.minQuantity || 1
      });
      if (r.errors) {
        toast.addNotification({
          type: 'error',
          title: r.errors.at(0)?.message || t('wishlists.failedAddToWishlist')
        });
      } else {
        toast.addNotification({
          title: listId === stockAlertWishlistId
            ? t('wishlist.addedToStockAlert')
            : t('wishlists.successfullyAddedToWishlist')
        });
      }
      return r;
    } catch (err) {
      toast.addNotification({
        type: 'error',
        title: err.info?.errors?.at(0)?.message || t('wishlists.failedAddToWishlist')
      });
    } finally {
      this.setState({ addToWishlistLoading: false });
    }
    return null;
  }

  handleOnChangeQuantity(qty, cb) {
    const { product } = this.state;
    const { onChangeQty } = this.props;
    const newQty = Math.min(
      product.stockValue,
      Math.max(qty, product.minQuantity || 1)
    );
    if (newQty !== product.quantity) {
      const newProduct = {
        ...product,
        quantity: newQty
      };
      this.setState({
        product: newProduct
      }, () => {
        if (typeof onChangeQty === 'function') {
          onChangeQty(newProduct);
        }
        if (typeof cb === 'function') {
          cb(newProduct);
        }
      });
    }
  }

  handleStockAlertModalOpen() {
    const { modalsContext } = this.props;
    const { product } = this.state;

    modalsContext.setState(modalsContext.modalState.STOCK_ALERT.key, {
      open: true,
      productId: product.id || product.productId,
      productCode: product.code,
      productQuantity: Math.max(product.quantity, product.minQuantity)
    });
  }

  render() {
    console.error('Render method has to be overwritten in extended class !');
    return null;
  }
}

ProductAbstract.propTypes = {
  product: oneOfType([ProductPropType, CartItemPropType]),
  toast: object,
  t: func,
  onChangeQty: func,
  onAddToCart: func,
  fetch: func,
  cart: object,
  modalsContext: object,
  wishlistContext: object,
  userContext: object
};
